import { Link, useNavigate } from "react-router-dom";
import { Phone, MapPin, Fuel, Users, Settings, Info, Sparkles, Tag, Zap, Percent, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../contexts/LanguageContext";
import { useCurrency } from "../contexts/CurrencyContext";
import { useCompare } from "../contexts/CompareContext";
import odooApi, { buildPhoneHref, buildWhatsAppUrl } from "../services/odooApi";
import CarImage from "./CarImage";
import WhatsAppIcon from "./WhatsAppIcon";

// Badge configurations
const BADGE_CONFIG = {
  offer: {
    icon: Percent,
    translationKey: "badges.offer",
    className: "bg-gradient-to-r from-orange-500 to-red-500",
  },
  exclusive: {
    icon: Sparkles,
    translationKey: "badges.exclusive",
    className: "bg-gradient-to-r from-purple-500 to-indigo-600",
  },
  new: {
    icon: Zap,
    translationKey: "badges.new",
    className: "bg-gradient-to-r from-emerald-400 to-teal-500",
  },
  discount: {
    icon: Tag,
    translationKey: "badges.discount",
    className: "bg-gradient-to-r from-amber-500 to-orange-500",
  },
};

export const CarCard = ({ car, badge, hideSupplier = true }) => {
  const navigate = useNavigate();
  const { t, tf } = useLanguage();
  const { formatPrice } = useCurrency();
  const { addToCompare, removeFromCompare, isInCompare, compareCars } = useCompare();
  
  const whatsappNumber = car.supplierWhatsApp || car.supplierPhone;
  const whatsappMessage = tf(
    "contact.whatsappCarPrefill",
    { car: `${car.brand} ${car.model}` },
    "Hi! I'm interested in renting the {car}. Please provide more details.",
  );
  const whatsappUrl = buildWhatsAppUrl(whatsappNumber, whatsappMessage, '');
  const callUrl = buildPhoneHref(car.supplierPhone, '');
  const compared = isInCompare(car.id);

  const handleCompareToggle = () => {
    if (compared) {
      removeFromCompare(car.id);
      toast.success(t("compare.removedToast", "Car removed from comparison."));
      return;
    }

    const result = addToCompare(car);
    if (!result.success) {
      toast.error(
        result.reason === "limit"
          ? t("compare.limitReached", "You can compare up to 4 cars at once.")
          : t("compare.alreadyAdded", "This car is already in your comparison tray."),
      );
      return;
    }

    toast.success(
      compareCars.length === 0
        ? t("compare.firstAdded", "Car added. Pick one more to open comparison.")
        : t("compare.addedToast", "Car added to comparison."),
    );
  };

  // Determine badge to show
  const displayBadge = badge || (car.discount_percent > 0 ? 'discount' : car.is_exclusive ? 'exclusive' : car.is_new ? 'new' : null);
  const badgeInfo = displayBadge ? BADGE_CONFIG[displayBadge] : null;
  const BadgeIcon = badgeInfo?.icon;
  const carDetailsPath = `/cars/${car.id}`;

  const handleCardClick = (event) => {
    if (event.target.closest("a, button, input, textarea, select, label")) {
      return;
    }

    navigate(carDetailsPath);
  };

  const handleCardKeyDown = (event) => {
    if (event.target.closest("a, button, input, textarea, select, label")) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      navigate(carDetailsPath);
    }
  };

  const handleContactClick = (event, type) => {
    event.stopPropagation();
    odooApi.trackClick({
      click_type: type,
      car_id: car.id,
      supplier_id: car.supplierId,
    });
  };

  return (
    <div
      className="car-card flex h-full flex-col cursor-pointer overflow-hidden rounded-xl border border-gray-100 bg-white transition-all hover:shadow-xl dark:border-gray-700 dark:bg-gray-800 hover:border-[#D4AF37] dark:hover:border-[#D4AF37]"
      data-testid={`car-card-${car.id}`}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="link"
      tabIndex={0}
    >
      {/* Image Container */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-100 dark:bg-gray-700">
        <CarImage
          src={car.image}
          alt={`${car.brand} ${car.model}`}
          className="car-image"
        />
        
        {/* Company Logo Watermark */}
        <div className="absolute bottom-3 right-3 opacity-60 group-hover:opacity-90 transition-opacity">
          <img 
            src="/velaris-logo.png" 
            alt={t("accessibility.logoAlt", "Velaris Royal Car Rental")} 
            className="h-6 w-auto object-contain drop-shadow-lg"
            style={{ filter: 'brightness(0) invert(1)' }}
          />
        </div>

        {/* Badge (Top Left Corner) */}
        {badgeInfo && BadgeIcon && (
          <div className={`absolute top-3 left-3 ${badgeInfo.className} px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg`}>
            <BadgeIcon className="w-3.5 h-3.5 text-white" />
            <span className="text-white text-xs font-bold uppercase tracking-wide">
              {car.discount_percent
                ? `${car.discount_percent}% OFF`
                : t(
                    badgeInfo.translationKey,
                    badgeInfo.translationKey.split(".").pop(),
                  )}
            </span>
          </div>
        )}

        {/* Price Badge */}
        <div className="absolute top-3 right-0 bg-gold-gradient px-4 py-2 rounded-l-lg shadow-lg">
          <span className="text-black font-bold text-sm">
            {formatPrice(car.pricePerDay)}/{t("common.day", "day")}
          </span>
          {car.originalPrice && car.originalPrice > car.pricePerDay && (
            <span className="block text-black/60 text-xs line-through">
              {formatPrice(car.originalPrice)}
            </span>
          )}
        </div>

        {/* Category Badge (Bottom Left) */}
        {car.category && (
          <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm px-3 py-1 rounded-full">
            <span className="text-white/90 text-xs uppercase tracking-wider font-medium">
              {car.category}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col p-5">
        {/* Title & Year */}
        <div className="mb-3">
          <Link to={carDetailsPath} className="block">
            <h3 className="font-[var(--font-display)] text-lg font-semibold text-gray-900 dark:text-white group-hover:text-[#D4AF37] transition-colors line-clamp-1">
              {car.brand} {car.model}
            </h3>
          </Link>
          <p className="text-gray-500 dark:text-gray-400 text-sm">{car.year}</p>
        </div>

        {/* Specs - Compact Pills */}
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs text-gray-600 dark:text-gray-300">
            <Users className="w-3 h-3" />
            {car.seats}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs text-gray-600 dark:text-gray-300">
            <Settings className="w-3 h-3" />
            {car.transmission?.substring(0, 4)}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs text-gray-600 dark:text-gray-300">
            <Fuel className="w-3 h-3" />
            {car.fuelType}
          </span>
        </div>

        {/* Location */}
        {car.city && (
          <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 mb-4">
            <MapPin className="w-3.5 h-3.5" />
            <span className="text-xs">{car.city}</span>
          </div>
        )}

        {/* Supplier - Hidden by default per requirements */}
        {!hideSupplier && car.supplier && (
          <div className="flex items-center gap-3 mb-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            {car.supplierLogo ? (
              <img src={car.supplierLogo} alt={car.supplier} className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                <span className="text-[#D4AF37] text-xs font-bold">{car.supplier?.substring(0, 2)}</span>
              </div>
            )}
            <span className="text-sm text-gray-600 dark:text-gray-300">{car.supplier}</span>
          </div>
        )}

        {/* Action Buttons - Improved UI */}
        <div className="mt-auto grid grid-cols-4 gap-2 pt-2">
          <Link
            to={carDetailsPath}
            className="flex h-11 w-full items-center justify-center rounded-lg bg-gray-100 text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
            data-testid={`details-btn-${car.id}`}
            title={t("btn.details", "Details")}
            aria-label={t("btn.details", "Details")}
          >
            <Info className="w-4 h-4" />
          </Link>
          {whatsappUrl !== '#' && (
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => handleContactClick(event, 'whatsapp')}
              className="flex h-11 w-full items-center justify-center rounded-lg bg-[#25D366] text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-[#1da851] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366]/40 focus-visible:ring-offset-2"
              data-testid={`whatsapp-btn-${car.id}`}
              title={t("btn.whatsapp", "WhatsApp")}
              aria-label={t("btn.whatsapp", "WhatsApp")}
            >
              <WhatsAppIcon className="w-4 h-4 shrink-0" />
            </a>
          )}
          <button
            type="button"
            onClick={handleCompareToggle}
            className={`flex h-11 w-full items-center justify-center rounded-lg border transition-colors ${
              compared
                ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]"
                : "border-gray-200 bg-white text-gray-600 hover:border-[#D4AF37] hover:text-[#D4AF37] dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
            }`}
            data-testid={`compare-btn-${car.id}`}
            title={compared ? t("compare.remove", "Remove from comparison") : t("compare.add", "Add to comparison")}
            aria-label={compared ? t("compare.remove", "Remove from comparison") : t("compare.add", "Add to comparison")}
          >
            <ArrowLeftRight className="w-4 h-4" />
          </button>
          {callUrl !== '#' && (
            <a
              href={callUrl}
              onClick={(event) => handleContactClick(event, 'call')}
              className="flex h-11 w-full items-center justify-center rounded-lg bg-[#D4AF37] text-black shadow-sm transition-colors hover:bg-[#C5A028] hover:shadow-md"
              data-testid={`call-btn-${car.id}`}
              title={t("btn.call", "Call")}
              aria-label={t("btn.call", "Call")}
            >
              <Phone className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default CarCard;
